import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Switch } from "@material-ui/core";
import { toast } from "react-toastify";
import {
  DeviceHub,
  Code,
  AllInclusive,
  ArrowForward,
  Close as CloseIcon,
  Payment as PaymentIcon,
  Phone as PhoneIcon,
  Help as HelpIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  Psychology as PsychologyIcon,
  Telegram as TelegramIcon,
  Speed as SpeedIcon,
  ShoppingCart as ShoppingCartIcon,
  Storefront as StorefrontIcon,
  MusicNote as MusicNoteIcon,
  Api as ApiIcon,
  SupportAgent as SupportAgentIcon,
} from "@mui/icons-material";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";

// Central de Apps - OpenAI e Gemini separados

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "calc(100vh - 64px)",
    overflowY: "auto",
  },
  header: {
    marginBottom: "32px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  title: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  subtitle: {
    fontSize: "14px",
    color: "#666",
    fontWeight: 400,
    margin: 0,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: "16px",
    marginTop: "24px",
    "@media (max-width: 1400px)": {
      gridTemplateColumns: "repeat(6, 1fr)",
    },
    "@media (max-width: 1200px)": {
      gridTemplateColumns: "repeat(4, 1fr)",
    },
    "@media (max-width: 900px)": {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
    "@media (max-width: 600px)": {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
  card: {
    background: "white",
    borderRadius: "10px",
    padding: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    transition: "all 0.3s ease",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    position: "relative",
    overflow: "hidden",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
      borderColor: "#3b82f6",
    },
  },
  cardDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
    "&:hover": {
      transform: "none",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      borderColor: "#e8ecf1",
    },
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
  },
  iconContainer: {
    width: "44px",
    height: "44px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "all 0.3s ease",
    "& svg": {
      fontSize: "22px",
    },
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    flex: 1,
  },
  appName: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
    lineHeight: 1.3,
  },
  appDescription: {
    fontSize: "12px",
    color: "#666",
    lineHeight: 1.4,
    margin: 0,
  },
  statusBadge: {
    position: "absolute",
    top: "10px",
    right: "10px",
    padding: "3px 10px",
    borderRadius: "16px",
    fontSize: "10px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statusAvailable: {
    background: "#d1fae5",
    color: "#065f46",
  },
  statusUnavailable: {
    background: "#fee2e2",
    color: "#991b1b",
  },
  statusComingSoon: {
    background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
    color: "#78350f",
    boxShadow: "0 2px 8px rgba(251, 191, 36, 0.3)",
    fontWeight: 700,
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "12px",
    borderTop: "1px solid #f0f0f0",
  },
  accessButton: {
    padding: "8px 16px",
    background: theme.palette.primary.main,
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    width: "100%",
    justifyContent: "center",
    "&:hover": {
      background: theme.palette.primary.dark,
      transform: "translateX(2px)",
    },
    "&:disabled": {
      background: "#e8ecf1",
      color: "#999",
      cursor: "not-allowed",
      transform: "none",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#999",
    fontSize: "14px",
  },
  searchContainer: {
    background: "white",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    marginBottom: "24px",
    display: "flex",
    gap: "16px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  searchInputWrapper: {
    flex: 1,
    minWidth: "250px",
  },
  searchInput: {
    width: "100%",
    padding: "12px 16px",
    border: "1px solid #e8ecf1",
    borderRadius: "8px",
    fontSize: "14px",
    fontFamily: "inherit",
    background: "#fafbfc",
    transition: "all 0.2s ease",
    "&:focus": {
      outline: "none",
      borderColor: "#3b82f6",
      background: "white",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
  },
  filterContainer: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  filterButton: {
    padding: "10px 18px",
    border: "1px solid #e8ecf1",
    background: "white",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 600,
    color: "#666",
    transition: "all 0.3s ease",
    whiteSpace: "nowrap",
    "&:hover": {
      background: "#f9fafb",
      borderColor: "#3b82f6",
      color: "#3b82f6",
    },
  },
  filterButtonActive: {
    background: "#3b82f6",
    color: "white",
    borderColor: "#3b82f6",
    "&:hover": {
      background: "#2563eb",
      borderColor: "#2563eb",
      color: "white",
    },
  },
  filterButtonConfigured: {
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "white",
    borderColor: "#10b981",
    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
    "&:hover": {
      background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
      borderColor: "#059669",
      color: "white",
      boxShadow: "0 4px 12px rgba(16, 185, 129, 0.4)",
    },
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "32px",
    gap: "12px",
    flexWrap: "wrap",
  },
  paginationButton: {
    padding: "8px 16px",
    border: "1px solid #e8ecf1",
    background: "white",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 600,
    color: "#1a1a1a",
    transition: "all 0.3s ease",
    minWidth: "40px",
    "&:hover": {
      background: "#f9fafb",
      borderColor: "#3b82f6",
    },
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
      "&:hover": {
        background: "white",
        borderColor: "#e8ecf1",
      },
    },
  },
  paginationButtonActive: {
    background: "#3b82f6",
    color: "white",
    borderColor: "#3b82f6",
    "&:hover": {
      background: "#2563eb",
      borderColor: "#2563eb",
    },
  },
  paginationInfo: {
    fontSize: "13px",
    color: "#666",
    padding: "0 12px",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: "20px",
  },
  modalContent: {
    background: "white",
    borderRadius: "12px",
    padding: "32px",
    maxWidth: "500px",
    width: "100%",
    maxHeight: "90vh",
    overflowY: "auto",
    position: "relative",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },
  closeButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "#f0f0f0",
    },
  },
  formGroup: {
    marginBottom: "20px",
  },
  label: {
    fontSize: "13px",
    color: "#374151",
    fontWeight: 600,
    display: "block",
    marginBottom: "8px",
  },
  input: {
    width: "100%",
    padding: "12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    fontFamily: "inherit",
    transition: "all 0.2s ease",
    "&:focus": {
      outline: "none",
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
  },
  helpText: {
    fontSize: "12px",
    color: "#6b7280",
    marginTop: "6px",
  },
  modalActions: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    marginTop: "24px",
  },
  buttonSecondary: {
    padding: "10px 20px",
    background: "#e8ecf1",
    color: "#1a1a1a",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "#d1d5db",
    },
  },
  buttonPrimary: {
    padding: "10px 20px",
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
    },
    "&:disabled": {
      background: "#9ca3af",
      cursor: "not-allowed",
      transform: "none",
    },
  },
  statusCard: {
    background: "#d4edda",
    border: "1px solid #c3e6cb",
    borderRadius: "8px",
    padding: "16px",
    color: "#155724",
    fontSize: "14px",
    marginBottom: "20px",
  },
}));

const Apps = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState("all"); // all, available, coming-soon, configured
  const itemsPerPage = 18; // 3 linhas x 6 colunas
  
  // Estados para o modal de configuração do CalCom
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [calcomApiKey, setCalcomApiKey] = useState("");
  const [calcomBaseUrl, setCalcomBaseUrl] = useState("https://api.cal.com/v2");
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [calcomIntegration, setCalcomIntegration] = useState(null);
  

  
  // Estados para Wavoip
  const [wavoipModalOpen, setWavoipModalOpen] = useState(false);
  const [wavoipUrl, setWavoipUrl] = useState("https://api.wavoip.com");
  const [wavoipUsername, setWavoipUsername] = useState("");
  const [wavoipPassword, setWavoipPassword] = useState("");
  const [wavoipConfigured, setWavoipConfigured] = useState(false);
  
  // Estados para WhatsApp Oficial
  const [whatsappOficialModalOpen, setWhatsappOficialModalOpen] = useState(false);
  const [whatsappOficialUrl, setWhatsappOficialUrl] = useState("");
  const [whatsappOficialToken, setWhatsappOficialToken] = useState("");
  const [whatsappOficialConfigured, setWhatsappOficialConfigured] = useState(false);
  
  // Estados para Telegram
  const [telegramModalOpen, setTelegramModalOpen] = useState(false);
  const [telegramBotToken, setTelegramBotToken] = useState("");
  const [telegramConfigured, setTelegramConfigured] = useState(false);
  
  // Estados para Groq
  const [groqModalOpen, setGroqModalOpen] = useState(false);
  const [groqApiKey, setGroqApiKey] = useState("");
  const [groqConfigured, setGroqConfigured] = useState(false);

  // Estados para DeepSeek
  const [deepseekModalOpen, setDeepseekModalOpen] = useState(false);
  const [deepseekApiKey, setDeepseekApiKey] = useState("");
  const [deepseekConfigured, setDeepseekConfigured] = useState(false);

  const [enableProfanityMonitor, setEnableProfanityMonitor] = useState(false);
  const [profanityWordList, setProfanityWordList] = useState("");
  const [savingProfanityList, setSavingProfanityList] = useState(false);
  
  // Estados para Mercado Livre
  const [mercadoLivreModalOpen, setMercadoLivreModalOpen] = useState(false);
  const [mercadoLivreAppId, setMercadoLivreAppId] = useState("");
  const [mercadoLivreSecretKey, setMercadoLivreSecretKey] = useState("");
  const [mercadoLivreConfigured, setMercadoLivreConfigured] = useState(false);
  
  // Estados para WooCommerce
  const [woocommerceModalOpen, setWoocommerceModalOpen] = useState(false);
  const [woocommerceUrl, setWoocommerceUrl] = useState("");
  const [woocommerceConsumerKey, setWoocommerceConsumerKey] = useState("");
  const [woocommerceConsumerSecret, setWoocommerceConsumerSecret] = useState("");
  const [woocommerceConfigured, setWoocommerceConfigured] = useState(false);
  
  // Estados para TikTok
  const [tiktokModalOpen, setTiktokModalOpen] = useState(false);
  const [tiktokAppId, setTiktokAppId] = useState("");
  const [tiktokAppSecret, setTiktokAppSecret] = useState("");
  const [tiktokConfigured, setTiktokConfigured] = useState(false);
  
  // Estados para NotificameHub
  const [notificamehubModalOpen, setNotificamehubModalOpen] = useState(false);
  const [notificamehubToken, setNotificamehubToken] = useState("");
  const [notificamehubConfigured, setNotificamehubConfigured] = useState(false);
  
  // Estados para IA (OpenAI e Gemini)
  const [openaiModalOpen, setOpenaiModalOpen] = useState(false);
  const [openaiApiKey, setOpenaiApiKey] = useState("");
  const [openaiConfigured, setOpenaiConfigured] = useState(false);
  
  const [geminiModalOpen, setGeminiModalOpen] = useState(false);
  const [geminiApiKey, setGeminiApiKey] = useState("");
  const [geminiConfigured, setGeminiConfigured] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);

      setShowIntegrations(planConfigs.plan.useIntegrations);
      setShowExternalApi(planConfigs.plan.useExternalApi);
      setShowOpenAi(planConfigs.plan.useOpenAi);
    }
    fetchData();
    fetchCalcomStatus();
    fetchPaymentSettings();
    fetchNotificamehubStatus();
    fetchIaSettings();
  }, [user.companyId]);

  const fetchCalcomStatus = async () => {
    try {
      const { data } = await api.get("/calcom/status");
      setCalcomIntegration(data);
    } catch (error) {
      // Integração não configurada
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const { data } = await api.get("/settings");
      
      // Verificar Wavoip - buscar das company settings
      const { data: companySettings } = await api.get(`/companySettings`);
      if (companySettings.wavoipUrl && companySettings.wavoipUsername) {
        setWavoipConfigured(true);
      }
    } catch (error) {
      console.error("Erro ao buscar configurações de pagamento:", error);
    }
  };

  const fetchNotificamehubStatus = async () => {
    try {
      const { data } = await api.get("/notificamehub/token");
      if (data.hasToken) {
        setNotificamehubConfigured(true);
      }
    } catch (error) {
      // Token não configurado
    }
  };

  const fetchIaSettings = async () => {
    try {
      const { data } = await api.get("/settings");
      
      // Verificar OpenAI
      const openaiSetting = data.find(s => s.key === "openaitoken");
      if (openaiSetting && openaiSetting.value) {
        setOpenaiConfigured(true);
      }
      
      // Verificar Gemini
      const geminiSetting = data.find(s => s.key === "geminitoken");
      if (geminiSetting && geminiSetting.value) {
        setGeminiConfigured(true);
      }
      
      // Verificar Groq
      const groqSetting = data.find(s => s.key === "groqtoken");
      if (groqSetting && groqSetting.value) {
        setGroqConfigured(true);
      }
      
      // Verificar DeepSeek
      const deepseekSetting = data.find(s => s.key === "deepseektoken");
      if (deepseekSetting && deepseekSetting.value) {
        setDeepseekConfigured(true);
      }
      
      // Verificar Monitoramento de Palavrões
      const profanitySetting = data.find(s => s.key === "enableProfanityMonitor");
      if (profanitySetting && profanitySetting.value === "true") {
        setEnableProfanityMonitor(true);
      }

      // Carregar lista personalizada de palavrões
      const profanityListSetting = data.find(s => s.key === "profanityWordList");
      if (profanityListSetting && profanityListSetting.value) {
        setProfanityWordList(profanityListSetting.value);
      }
    } catch (error) {
      console.error("Erro ao buscar configurações de IA:", error);
    }
  };

  const handleConfigureCalcom = async () => {
    if (!calcomApiKey.trim()) {
      toast.error("API Key é obrigatória");
      return;
    }

    setLoadingConfig(true);
    try {
      await api.post("/calcom/configure", { 
        apiKey: calcomApiKey, 
        baseUrl: calcomBaseUrl 
      });
      toast.success("Integração Cal.com configurada com sucesso!");
      await fetchCalcomStatus();
      setConfigModalOpen(false);
      setCalcomApiKey("");
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Chave de API inválida. Verifique suas credenciais no Cal.com");
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("Erro ao configurar integração. Tente novamente.");
      }
      console.error("Erro na configuração:", error);
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleConfigureWavoip = async () => {
    if (!wavoipUrl.trim() || !wavoipUsername.trim() || !wavoipPassword.trim()) {
      toast.error("URL, Email/Usuário e Senha são obrigatórios");
      return;
    }

    setLoadingConfig(true);
    try {
      // Atualizar as 3 configurações
      await api.put(`/companySettings`, { 
        column: "wavoipUrl",
        data: wavoipUrl 
      });
      await api.put(`/companySettings`, { 
        column: "wavoipUsername",
        data: wavoipUsername 
      });
      await api.put(`/companySettings`, { 
        column: "wavoipPassword",
        data: wavoipPassword 
      });
      
      toast.success("Wavoip configurado com sucesso!");
      await fetchPaymentSettings();
      setWavoipModalOpen(false);
      setWavoipUrl("https://api.wavoip.com");
      setWavoipUsername("");
      setWavoipPassword("");
    } catch (error) {
      toast.error("Erro ao configurar Wavoip: " + (error.response?.data?.error || error.message));
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleConfigureNotificamehub = async () => {
    if (!notificamehubToken.trim()) {
      toast.error("Token é obrigatório");
      return;
    }

    setLoadingConfig(true);
    try {
      await api.post("/notificamehub/token", { token: notificamehubToken.trim() });
      toast.success("NotificameHub configurado com sucesso!");
      await fetchNotificamehubStatus();
      setNotificamehubModalOpen(false);
      setNotificamehubToken("");
    } catch (error) {
      toast.error("Erro ao configurar NotificameHub: " + (error.response?.data?.error || error.message));
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleConfigureOpenAI = async () => {
    if (!openaiApiKey.trim()) {
      toast.error("API Key é obrigatória");
      return;
    }

    setLoadingConfig(true);
    try {
      await api.put(`/settings/openaitoken`, { value: openaiApiKey.trim() });
      toast.success("OpenAI configurado com sucesso!");
      await fetchIaSettings();
      setOpenaiModalOpen(false);
      setOpenaiApiKey("");
    } catch (error) {
      toast.error("Erro ao configurar OpenAI: " + (error.response?.data?.error || error.message));
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleConfigureGemini = async () => {
    if (!geminiApiKey.trim()) {
      toast.error("API Key é obrigatória");
      return;
    }

    setLoadingConfig(true);
    try {
      await api.put(`/settings/geminitoken`, { value: geminiApiKey.trim() });
      toast.success("Google Gemini configurado com sucesso!");
      await fetchIaSettings();
      setGeminiModalOpen(false);
      setGeminiApiKey("");
    } catch (error) {
      toast.error("Erro ao configurar Gemini: " + (error.response?.data?.error || error.message));
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleConfigureGroq = async () => {
    const trimmedKey = groqApiKey.trim();
    
    if (!trimmedKey) {
      toast.error("API Key é obrigatória");
      return;
    }

    // Validar formato da chave Groq
    if (!trimmedKey.startsWith('gsk_')) {
      toast.error("❌ Chave inválida! A chave do Groq deve começar com 'gsk_'");
      return;
    }

    if (trimmedKey.length < 40 || trimmedKey.length > 100) {
      toast.error(`❌ Chave inválida! Tamanho esperado: 40-100 caracteres. Recebido: ${trimmedKey.length}`);
      return;
    }

    console.log("[GROQ CONFIG] API Key length:", trimmedKey.length);
    console.log("[GROQ CONFIG] API Key first 20 chars:", trimmedKey.substring(0, 20));

    setLoadingConfig(true);
    try {
      await api.put(`/settings/groqtoken`, { value: trimmedKey });
      toast.success("✅ Groq AI configurado com sucesso!");
      await fetchIaSettings();
      setGroqModalOpen(false);
      setGroqApiKey("");
    } catch (error) {
      toast.error("Erro ao configurar Groq: " + (error.response?.data?.error || error.message));
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleConfigureDeepSeek = async () => {
    if (!deepseekApiKey.trim()) {
      toast.error("API Key é obrigatória");
      return;
    }

    setLoadingConfig(true);
    try {
      await api.put(`/settings/deepseektoken`, { value: deepseekApiKey.trim() });
      toast.success("DeepSeek configurado com sucesso!");
      await fetchIaSettings();
      setDeepseekModalOpen(false);
      setDeepseekApiKey("");
    } catch (error) {
      toast.error("Erro ao configurar DeepSeek: " + (error.response?.data?.error || error.message));
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleToggleProfanityMonitor = async (value) => {
    setEnableProfanityMonitor(value);
    try {
      await api.put(`/settings/enableProfanityMonitor`, { value: value ? "true" : "false" });
      toast.success(value ? "Monitoramento de palavrões ativado!" : "Monitoramento de palavrões desativado!");
    } catch (error) {
      toast.error("Erro ao atualizar configuração: " + (error.response?.data?.error || error.message));
      setEnableProfanityMonitor(!value);
    }
  };

  const handleSaveProfanityList = async () => {
    setSavingProfanityList(true);
    try {
      await api.put(`/settings/profanityWordList`, { value: profanityWordList });
      toast.success("✅ Lista de palavrões salva com sucesso!");
    } catch (error) {
      toast.error("Erro ao salvar lista: " + (error.response?.data?.error || error.message));
    } finally {
      setSavingProfanityList(false);
    }
  };

  const apps = [
    {
      id: "suporte",
      name: "Suporte",
      description: "Abra tickets de suporte para reportar problemas e acompanhar o atendimento",
      icon: <SupportAgentIcon />,
      enabled: true,
      color: "#673ab7",
      hasConfig: false,
      route: "/support",
    },
    {
      id: "whatsapp-oficial",
      name: "WhatsApp Oficial",
      description: "Configure a API oficial do WhatsApp Business para envio de mensagens",
      icon: <WhatsAppIcon />,
      enabled: false,
      color: "#25d366",
      hasConfig: true,
      comingSoon: true,
    },
    {
      id: "telegram",
      name: "Telegram",
      description: "Configure integração com Telegram para atendimento via bot",
      icon: <TelegramIcon />,
      enabled: false,
      color: "#0088cc",
      hasConfig: true,
      comingSoon: true,
    },
    {
      id: "groq",
      name: "Groq AI",
      description: "Configure API do Groq para IA ultrarrápida com modelos Llama e Mixtral",
      icon: <SpeedIcon />,
      enabled: true,
      color: "#f55036",
      hasConfig: true,
    },
    {
      id: "deepseek",
      name: "DeepSeek",
      description: "Configure API do DeepSeek para usar modelos de IA avançados",
      icon: <PsychologyIcon />,
      enabled: true,
      color: "#1e40af",
      hasConfig: true,
    },
    {
      id: "mercadolivre",
      name: "Mercado Livre",
      description: "Integre com Mercado Livre para gerenciar vendas e atendimento",
      icon: <ShoppingCartIcon />,
      enabled: false,
      color: "#ffe600",
      hasConfig: true,
      comingSoon: true,
    },
    {
      id: "woocommerce",
      name: "WooCommerce",
      description: "Conecte sua loja WooCommerce para sincronizar pedidos e clientes",
      icon: <StorefrontIcon />,
      enabled: false,
      color: "#96588a",
      hasConfig: true,
      comingSoon: true,
    },
    {
      id: "tiktok",
      name: "TikTok",
      description: "Configure integração com TikTok para atendimento e mensagens",
      icon: <MusicNoteIcon />,
      enabled: false,
      color: "#000000",
      hasConfig: true,
      comingSoon: true,
    },
    {
      id: "openai",
      name: "OpenAI (ChatGPT)",
      description: "Configure API Key do OpenAI para usar GPT-3.5, GPT-4 e outros modelos no FlowBuilder",
      icon: <PsychologyIcon />,
      enabled: true,
      color: "#10a37f",
      hasConfig: true,
    },
    {
      id: "gemini",
      name: "Google Gemini",
      description: "Configure API Key do Google Gemini para usar IA do Google no FlowBuilder",
      icon: <PsychologyIcon />,
      enabled: true,
      color: "#4285f4",
      hasConfig: true,
    },
    {
      id: "notificamehub",
      name: "NotificameHub",
      description: "Configure canais de comunicação WhatsApp, Instagram e Facebook via NotificameHub",
      icon: <NotificationsIcon />,
      enabled: true,
      color: "#9333ea",
      hasConfig: true,
    },
    {
      id: "calcom",
      name: "Cal Agenda",
      description: "Configure sua integração com Cal.com para gerenciar agendamentos",
      icon: <DeviceHub />,
      route: "/calcom-integration",
      enabled: true,
      color: "#8b5cf6",
      hasConfig: true,
    },
    {
      id: "wacalls",
      name: "VoIP",
      description: "Chamadas de voz via WhatsApp com gravação, sessões e histórico completo",
      icon: <PhoneIcon />,
      route: "/voip",
      enabled: true,
      color: "#16a34a",
    },
    {
      id: "wavoip",
      name: "Wavoip",
      description: "Configure sua integração com Wavoip para chamadas VoIP",
      icon: <PhoneIcon />,
      enabled: true,
      color: "#f59e0b",
      hasConfig: true,
    },
    {
      id: "helps",
      name: "Treinamento",
      description: "Acesse tutoriais, documentação e suporte para usar o sistema",
      icon: <HelpIcon />,
      route: "/helps",
      enabled: true,
      color: "#06b6d4",
    },
    {
      id: "integration",
      name: "Integração",
      description: "Configure integrações com sistemas externos e automatize processos",
      icon: <DeviceHub />,
      route: "/queue-integration",
      enabled: showIntegrations,
      color: "#3b82f6",
    },
    {
      id: "api",
      name: "API",
      description: "Gerencie suas chaves de API e configure endpoints personalizados",
      icon: <Code />,
      route: "/messages-api",
      enabled: showExternalApi,
      color: "#8b5cf6",
    },
  ];

  const handleAppClick = (app) => {
    if (app.comingSoon) {
      toast.info("🎉 Novidade em breve! Esta funcionalidade estará disponível em breve.");
      return;
    }
    
    if (app.enabled) {
      if (app.hasConfig) {
        if (app.id === "openai") {
          setOpenaiModalOpen(true);
        } else if (app.id === "gemini") {
          setGeminiModalOpen(true);
        } else if (app.id === "notificamehub") {
          setNotificamehubModalOpen(true);
        } else if (app.id === "calcom") {
          setConfigModalOpen(true);
        } else if (app.id === "wavoip") {
          setWavoipModalOpen(true);
        } else if (app.id === "whatsapp-oficial") {
          setWhatsappOficialModalOpen(true);
        } else if (app.id === "telegram") {
          setTelegramModalOpen(true);
        } else if (app.id === "groq") {
          setGroqModalOpen(true);
        } else if (app.id === "deepseek") {
          setDeepseekModalOpen(true);
        } else if (app.id === "mercadolivre") {
          setMercadoLivreModalOpen(true);
        } else if (app.id === "woocommerce") {
          setWoocommerceModalOpen(true);
        } else if (app.id === "tiktok") {
          setTiktokModalOpen(true);
        }
      } else {
        history.push(app.route);
      }
    }
  };

  // Filtrar apps baseado na busca e status
  const filteredApps = apps.filter((app) => {
    const matchesSearch = app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterStatus === "all") {
      return matchesSearch;
    } else if (filterStatus === "available") {
      return matchesSearch && app.enabled && !app.comingSoon;
    } else if (filterStatus === "coming-soon") {
      return matchesSearch && app.comingSoon;
    } else if (filterStatus === "configured") {
      // Verificar se o app está configurado
      const isConfigured = 
        (app.id === "openai" && openaiConfigured) ||
        (app.id === "gemini" && geminiConfigured) ||
        (app.id === "groq" && groqConfigured) ||
        (app.id === "deepseek" && deepseekConfigured) ||
        (app.id === "notificamehub" && notificamehubConfigured) ||
        (app.id === "calcom" && calcomIntegration?.configured) ||
        (app.id === "wavoip" && wavoipConfigured);
      return matchesSearch && isConfigured;
    }
    
    return matchesSearch;
  });

  // Calcular paginação
  const totalPages = Math.ceil(filteredApps.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedApps = filteredApps.slice(startIndex, endIndex);

  // Reset para página 1 quando buscar ou filtrar
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (status) => {
    setFilterStatus(status);
    setCurrentPage(1);
  };

  return (
    <MainContainer>
      <div className={classes.container}>
        {/* Header */}
        <div className={classes.header}>
          <h1 className={classes.title}>
            🚀 Central de Apps
            <span style={{
              fontSize: "16px",
              fontWeight: 600,
              background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
              color: "white",
              padding: "4px 12px",
              borderRadius: "20px",
              marginLeft: "12px",
            }}>
              {filteredApps.length} {filteredApps.length === 1 ? "app" : "apps"}
            </span>
          </h1>
          <p className={classes.subtitle}>
            Explore e acesse todos os aplicativos e integrações disponíveis para sua empresa
          </p>
        </div>

        {/* Busca e Filtros */}
        <div className={classes.searchContainer}>
          <div className={classes.searchInputWrapper}>
            <input
              type="text"
              placeholder="🔍 Buscar aplicativos..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className={classes.searchInput}
            />
          </div>
          
          <div className={classes.filterContainer}>
            <button
              className={`${classes.filterButton} ${filterStatus === "all" ? classes.filterButtonActive : ""}`}
              onClick={() => handleFilterChange("all")}
            >
              Todos
            </button>
            <button
              className={`${classes.filterButton} ${filterStatus === "available" ? classes.filterButtonActive : ""}`}
              onClick={() => handleFilterChange("available")}
            >
              Disponíveis
            </button>
            <button
              className={`${classes.filterButton} ${
                filterStatus === "configured" 
                  ? classes.filterButtonConfigured 
                  : ""
              }`}
              onClick={() => handleFilterChange("configured")}
            >
              ✓ Configurados
            </button>
            <button
              className={`${classes.filterButton} ${filterStatus === "coming-soon" ? classes.filterButtonActive : ""}`}
              onClick={() => handleFilterChange("coming-soon")}
            >
              🎉 Novidades
            </button>
          </div>
        </div>

        {/* Grid de Apps */}
        <div className={classes.grid}>
          {paginatedApps.map((app) => (
            <div
              key={app.id}
              className={`${classes.card} ${!app.enabled ? classes.cardDisabled : ""}`}
              onClick={() => handleAppClick(app)}
            >
              {/* Status Badge */}
              <span
                className={`${classes.statusBadge} ${
                  app.comingSoon 
                    ? classes.statusComingSoon
                    : app.enabled 
                      ? classes.statusAvailable 
                      : classes.statusUnavailable
                }`}
              >
                {app.comingSoon 
                  ? "🎉 Novidade em Breve"
                  : app.enabled 
                    ? (app.id === "calcom" && calcomIntegration?.configured 
                        ? "Configurado" 
                        : "Disponível")
                    : "Indisponível"}
              </span>

              {/* Card Header com Ícone */}
              <div className={classes.cardHeader}>
                <div
                  className={classes.iconContainer}
                  style={{
                    background: app.enabled
                      ? `linear-gradient(135deg, ${app.color}20 0%, ${app.color}40 100%)`
                      : "rgba(0,0,0,0.05)",
                  }}
                >
                  {React.cloneElement(app.icon, {
                    style: { 
                      color: app.enabled ? app.color : "#999",
                      fontSize: "22px"
                    },
                  })}
                </div>
              </div>

              {/* Card Content */}
              <div className={classes.cardContent}>
                <h3 className={classes.appName}>{app.name}</h3>
                <p className={classes.appDescription}>{app.description}</p>
                
                {/* Status Ativo para apps configurados */}
                {app.id === "openai" && openaiConfigured && (
                  <div style={{
                    marginTop: "8px",
                    padding: "4px 10px",
                    background: "#d4edda",
                    border: "1px solid #c3e6cb",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#155724",
                    display: "inline-block",
                  }}>
                    ✓ Ativo
                  </div>
                )}
                {app.id === "gemini" && geminiConfigured && (
                  <div style={{
                    marginTop: "8px",
                    padding: "4px 10px",
                    background: "#d4edda",
                    border: "1px solid #c3e6cb",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#155724",
                    display: "inline-block",
                  }}>
                    ✓ Ativo
                  </div>
                )}
                {app.id === "groq" && groqConfigured && (
                  <div style={{
                    marginTop: "8px",
                    padding: "4px 10px",
                    background: "#d4edda",
                    border: "1px solid #c3e6cb",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#155724",
                    display: "inline-block",
                  }}>
                    ✓ Ativo
                  </div>
                )}
                {app.id === "deepseek" && deepseekConfigured && (
                  <div style={{
                    marginTop: "8px",
                    padding: "4px 10px",
                    background: "#d4edda",
                    border: "1px solid #c3e6cb",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#155724",
                    display: "inline-block",
                  }}>
                    ✓ Ativo
                  </div>
                )}
                {app.id === "notificamehub" && notificamehubConfigured && (
                  <div style={{
                    marginTop: "8px",
                    padding: "4px 10px",
                    background: "#d4edda",
                    border: "1px solid #c3e6cb",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#155724",
                    display: "inline-block",
                  }}>
                    ✓ Ativo
                  </div>
                )}
                {app.id === "calcom" && calcomIntegration?.configured && (
                  <div style={{
                    marginTop: "8px",
                    padding: "4px 10px",
                    background: "#d4edda",
                    border: "1px solid #c3e6cb",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#155724",
                    display: "inline-block",
                  }}>
                    ✓ Ativo
                  </div>
                )}
                {app.id === "wavoip" && wavoipConfigured && (
                  <div style={{
                    marginTop: "8px",
                    padding: "4px 10px",
                    background: "#d4edda",
                    border: "1px solid #c3e6cb",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#155724",
                    display: "inline-block",
                  }}>
                    ✓ Ativo
                  </div>
                )}
              </div>

              {/* Card Footer com Botão */}
              <div className={classes.cardFooter}>
                <button
                  className={classes.accessButton}
                  disabled={!app.enabled && !app.comingSoon}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAppClick(app);
                  }}
                >
                  {app.comingSoon ? (
                    <>
                      Novidade em Breve <ArrowForward style={{ fontSize: "14px" }} />
                    </>
                  ) : app.enabled ? (
                    <>
                      {app.hasConfig ? "Configurar" : "Acessar"} <ArrowForward style={{ fontSize: "14px" }} />
                    </>
                  ) : (
                    "Indisponível"
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredApps.length === 0 && (
          <div className={classes.emptyState}>
            <p>
              {searchTerm
                ? `Nenhum aplicativo encontrado para "${searchTerm}"`
                : "Nenhum aplicativo disponível no momento"}
            </p>
          </div>
        )}

        {/* Paginação */}
        {filteredApps.length > itemsPerPage && (
          <div className={classes.paginationContainer}>
            <button
              className={classes.paginationButton}
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ← Anterior
            </button>

            <span className={classes.paginationInfo}>
              Página {currentPage} de {totalPages}
            </span>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={`${classes.paginationButton} ${
                  currentPage === page ? classes.paginationButtonActive : ""
                }`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}

            <button
              className={classes.paginationButton}
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Próxima →
            </button>
          </div>
        )}

        {/* Modal de Configuração OpenAI */}
        {openaiModalOpen && (
          <div className={classes.modalOverlay} onClick={() => setOpenaiModalOpen(false)}>
            <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={classes.modalHeader}>
                <h2 className={classes.modalTitle}>🤖 Configurar OpenAI (ChatGPT)</h2>
                <button
                  className={classes.closeButton}
                  onClick={() => setOpenaiModalOpen(false)}
                >
                  <CloseIcon style={{ fontSize: "20px", color: "#6b7280" }} />
                </button>
              </div>

              {openaiConfigured && (
                <div className={classes.statusCard}>
                  ✓ OpenAI configurado e ativo!
                </div>
              )}

              <div style={{ marginBottom: "20px", padding: "12px", background: "#f0fdf4", borderRadius: "8px", border: "1px solid #bbf7d0" }}>
                <p style={{ margin: 0, fontSize: "13px", color: "#15803d" }}>
                  Configure a API Key do OpenAI para usar modelos como GPT-3.5, GPT-4, GPT-4o no FlowBuilder.
                </p>
              </div>

              <div className={classes.formGroup}>
                <label className={classes.label}>OpenAI API Key</label>
                <input
                  type="password"
                  value={openaiApiKey}
                  onChange={(e) => setOpenaiApiKey(e.target.value)}
                  placeholder="sk-..."
                  className={classes.input}
                />
                <div className={classes.helpText}>
                  Obtenha sua chave em: <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" style={{ color: "#10a37f" }}>platform.openai.com/api-keys</a>
                </div>
              </div>

              <div className={classes.modalActions}>
                <button
                  className={classes.buttonSecondary}
                  onClick={() => setOpenaiModalOpen(false)}
                >
                  Cancelar
                </button>
                <button
                  className={classes.buttonPrimary}
                  onClick={handleConfigureOpenAI}
                  disabled={loadingConfig}
                >
                  {loadingConfig ? "Salvando..." : "Salvar Configuração"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Configuração Google Gemini */}
        {geminiModalOpen && (
          <div className={classes.modalOverlay} onClick={() => setGeminiModalOpen(false)}>
            <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={classes.modalHeader}>
                <h2 className={classes.modalTitle}>✨ Configurar Google Gemini</h2>
                <button
                  className={classes.closeButton}
                  onClick={() => setGeminiModalOpen(false)}
                >
                  <CloseIcon style={{ fontSize: "20px", color: "#6b7280" }} />
                </button>
              </div>

              {geminiConfigured && (
                <div className={classes.statusCard}>
                  ✓ Google Gemini configurado e ativo!
                </div>
              )}

              <div style={{ marginBottom: "20px", padding: "12px", background: "#eff6ff", borderRadius: "8px", border: "1px solid #bfdbfe" }}>
                <p style={{ margin: 0, fontSize: "13px", color: "#1e40af" }}>
                  Configure a API Key do Google Gemini para usar a IA do Google no FlowBuilder.
                </p>
              </div>

              <div className={classes.formGroup}>
                <label className={classes.label}>Google Gemini API Key</label>
                <input
                  type="password"
                  value={geminiApiKey}
                  onChange={(e) => setGeminiApiKey(e.target.value)}
                  placeholder="AIza..."
                  className={classes.input}
                />
                <div className={classes.helpText}>
                  Obtenha sua chave em: <a href="https://makersuite.google.com/app/apikey" target="_blank" rel="noopener noreferrer" style={{ color: "#4285f4" }}>makersuite.google.com/app/apikey</a>
                </div>
              </div>

              <div className={classes.modalActions}>
                <button
                  className={classes.buttonSecondary}
                  onClick={() => setGeminiModalOpen(false)}
                >
                  Cancelar
                </button>
                <button
                  className={classes.buttonPrimary}
                  onClick={handleConfigureGemini}
                  disabled={loadingConfig}
                >
                  {loadingConfig ? "Salvando..." : "Salvar Configuração"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Configuração Telegram */}
        {telegramModalOpen && (
          <div className={classes.modalOverlay} onClick={() => setTelegramModalOpen(false)}>
            <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={classes.modalHeader}>
                <h2 className={classes.modalTitle}>📱 Configurar Telegram</h2>
                <button className={classes.closeButton} onClick={() => setTelegramModalOpen(false)}>
                  <CloseIcon style={{ fontSize: "20px", color: "#6b7280" }} />
                </button>
              </div>
              <div style={{ background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: "8px", padding: "16px", color: "#92400e", fontSize: "14px", marginBottom: "20px" }}>
                ⚠️ Esta funcionalidade estará disponível em breve!
              </div>
              <div className={classes.formGroup}>
                <label className={classes.label}>Bot Token</label>
                <input type="password" value={telegramBotToken} onChange={(e) => setTelegramBotToken(e.target.value)} placeholder="123456:ABC-DEF..." className={classes.input} disabled style={{ background: "#f9fafb", cursor: "not-allowed" }} />
                <div className={classes.helpText}>Token do bot criado no @BotFather do Telegram</div>
              </div>
              <div className={classes.modalActions}>
                <button className={classes.buttonSecondary} onClick={() => setTelegramModalOpen(false)}>Fechar</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Configuração Groq */}
        {groqModalOpen && (
          <div className={classes.modalOverlay} onClick={() => setGroqModalOpen(false)}>
            <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={classes.modalHeader}>
                <h2 className={classes.modalTitle}>⚡ Configurar Groq AI</h2>
                <button className={classes.closeButton} onClick={() => setGroqModalOpen(false)}>
                  <CloseIcon style={{ fontSize: "20px", color: "#6b7280" }} />
                </button>
              </div>
              <div className={classes.formGroup}>
                <label className={classes.label}>Groq API Key</label>
                <input type="password" value={groqApiKey} onChange={(e) => setGroqApiKey(e.target.value)} placeholder="gsk_..." className={classes.input} />
                <div className={classes.helpText}>
                  API Key para usar modelos Llama e Mixtral ultrarrápidos. Obtenha em: <a href="https://console.groq.com" target="_blank" rel="noopener noreferrer" style={{ color: "#f55036" }}>console.groq.com</a>
                </div>
              </div>
              <div className={classes.formGroup}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px", background: "#f9fafb", borderRadius: "8px", border: "1px solid #e8ecf1" }}>
                  <label className={classes.label} style={{ margin: 0 }}>Ativar Monitoramento de Palavrões</label>
                  <Switch
                    checked={enableProfanityMonitor}
                    onChange={(e) => handleToggleProfanityMonitor(e.target.checked)}
                    color="primary"
                  />
                </div>
              </div>
              {enableProfanityMonitor && (
                <div className={classes.formGroup}>
                  <label className={classes.label}>Lista Personalizada de Palavrões</label>
                  <textarea
                    value={profanityWordList}
                    onChange={(e) => setProfanityWordList(e.target.value)}
                    placeholder={"Insira palavras separadas por vírgula.\nEx: palavra1, palavra2, expressão ofensiva, palavra3"}
                    className={classes.input}
                    style={{ minHeight: "120px", resize: "vertical", fontFamily: "monospace", fontSize: "13px" }}
                  />
                  <div className={classes.helpText}>
                    Adicione palavras ou expressões separadas por vírgula. O sistema já possui uma lista padrão embutida — as palavras adicionadas aqui serão somadas à lista padrão.
                  </div>
                  <button
                    className={classes.buttonPrimary}
                    onClick={handleSaveProfanityList}
                    disabled={savingProfanityList}
                    style={{ marginTop: "8px", width: "auto", alignSelf: "flex-start" }}
                  >
                    {savingProfanityList ? "Salvando..." : "💾 Salvar Lista"}
                  </button>
                </div>
              )}
              <div className={classes.modalActions}>
                <button className={classes.buttonSecondary} onClick={() => setGroqModalOpen(false)}>Cancelar</button>
                <button className={classes.buttonPrimary} onClick={handleConfigureGroq} disabled={loadingConfig}>
                  {loadingConfig ? "Salvando..." : "Salvar Configuração"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Configuração DeepSeek */}
        {deepseekModalOpen && (
          <div className={classes.modalOverlay} onClick={() => setDeepseekModalOpen(false)}>
            <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={classes.modalHeader}>
                <h2 className={classes.modalTitle}>🧠 Configurar DeepSeek</h2>
                <button className={classes.closeButton} onClick={() => setDeepseekModalOpen(false)}>
                  <CloseIcon style={{ fontSize: "20px", color: "#6b7280" }} />
                </button>
              </div>
              <div className={classes.formGroup}>
                <label className={classes.label}>DeepSeek API Key</label>
                <input type="password" value={deepseekApiKey} onChange={(e) => setDeepseekApiKey(e.target.value)} placeholder="sk_..." className={classes.input} />
                <div className={classes.helpText}>
                  API Key para usar modelos DeepSeek avançados. Obtenha em: <a href="https://platform.deepseek.com" target="_blank" rel="noopener noreferrer" style={{ color: "#1e40af" }}>platform.deepseek.com</a>
                </div>
              </div>
              <div className={classes.modalActions}>
                <button className={classes.buttonSecondary} onClick={() => setDeepseekModalOpen(false)}>Cancelar</button>
                <button className={classes.buttonPrimary} onClick={handleConfigureDeepSeek} disabled={loadingConfig}>
                  {loadingConfig ? "Salvando..." : "Salvar Configuração"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Configuração Mercado Livre */}
        {mercadoLivreModalOpen && (
          <div className={classes.modalOverlay} onClick={() => setMercadoLivreModalOpen(false)}>
            <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={classes.modalHeader}>
                <h2 className={classes.modalTitle}>🛒 Configurar Mercado Livre</h2>
                <button className={classes.closeButton} onClick={() => setMercadoLivreModalOpen(false)}>
                  <CloseIcon style={{ fontSize: "20px", color: "#6b7280" }} />
                </button>
              </div>
              <div style={{ background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: "8px", padding: "16px", color: "#92400e", fontSize: "14px", marginBottom: "20px" }}>
                ⚠️ Esta funcionalidade estará disponível em breve!
              </div>
              <div className={classes.formGroup}>
                <label className={classes.label}>App ID</label>
                <input type="text" value={mercadoLivreAppId} onChange={(e) => setMercadoLivreAppId(e.target.value)} placeholder="Seu App ID" className={classes.input} disabled style={{ background: "#f9fafb", cursor: "not-allowed" }} />
                <div className={classes.helpText}>ID do aplicativo criado no Mercado Livre Developers</div>
              </div>
              <div className={classes.formGroup}>
                <label className={classes.label}>Secret Key</label>
                <input type="password" value={mercadoLivreSecretKey} onChange={(e) => setMercadoLivreSecretKey(e.target.value)} placeholder="Sua Secret Key" className={classes.input} disabled style={{ background: "#f9fafb", cursor: "not-allowed" }} />
                <div className={classes.helpText}>Chave secreta do aplicativo</div>
              </div>
              <div className={classes.modalActions}>
                <button className={classes.buttonSecondary} onClick={() => setMercadoLivreModalOpen(false)}>Fechar</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Configuração WooCommerce */}
        {woocommerceModalOpen && (
          <div className={classes.modalOverlay} onClick={() => setWoocommerceModalOpen(false)}>
            <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={classes.modalHeader}>
                <h2 className={classes.modalTitle}>🏪 Configurar WooCommerce</h2>
                <button className={classes.closeButton} onClick={() => setWoocommerceModalOpen(false)}>
                  <CloseIcon style={{ fontSize: "20px", color: "#6b7280" }} />
                </button>
              </div>
              <div style={{ background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: "8px", padding: "16px", color: "#92400e", fontSize: "14px", marginBottom: "20px" }}>
                ⚠️ Esta funcionalidade estará disponível em breve!
              </div>
              <div className={classes.formGroup}>
                <label className={classes.label}>URL da Loja</label>
                <input type="text" value={woocommerceUrl} onChange={(e) => setWoocommerceUrl(e.target.value)} placeholder="https://minhaloja.com.br" className={classes.input} disabled style={{ background: "#f9fafb", cursor: "not-allowed" }} />
                <div className={classes.helpText}>URL completa da sua loja WooCommerce</div>
              </div>
              <div className={classes.formGroup}>
                <label className={classes.label}>Consumer Key</label>
                <input type="text" value={woocommerceConsumerKey} onChange={(e) => setWoocommerceConsumerKey(e.target.value)} placeholder="ck_..." className={classes.input} disabled style={{ background: "#f9fafb", cursor: "not-allowed" }} />
                <div className={classes.helpText}>Chave de consumidor da API REST</div>
              </div>
              <div className={classes.formGroup}>
                <label className={classes.label}>Consumer Secret</label>
                <input type="password" value={woocommerceConsumerSecret} onChange={(e) => setWoocommerceConsumerSecret(e.target.value)} placeholder="cs_..." className={classes.input} disabled style={{ background: "#f9fafb", cursor: "not-allowed" }} />
                <div className={classes.helpText}>Chave secreta da API REST</div>
              </div>
              <div className={classes.modalActions}>
                <button className={classes.buttonSecondary} onClick={() => setWoocommerceModalOpen(false)}>Fechar</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Configuração TikTok */}
        {tiktokModalOpen && (
          <div className={classes.modalOverlay} onClick={() => setTiktokModalOpen(false)}>
            <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={classes.modalHeader}>
                <h2 className={classes.modalTitle}>🎵 Configurar TikTok</h2>
                <button className={classes.closeButton} onClick={() => setTiktokModalOpen(false)}>
                  <CloseIcon style={{ fontSize: "20px", color: "#6b7280" }} />
                </button>
              </div>
              <div style={{ background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: "8px", padding: "16px", color: "#92400e", fontSize: "14px", marginBottom: "20px" }}>
                ⚠️ Esta funcionalidade estará disponível em breve!
              </div>
              <div className={classes.formGroup}>
                <label className={classes.label}>App ID</label>
                <input type="text" value={tiktokAppId} onChange={(e) => setTiktokAppId(e.target.value)} placeholder="Seu App ID do TikTok" className={classes.input} disabled style={{ background: "#f9fafb", cursor: "not-allowed" }} />
                <div className={classes.helpText}>ID do aplicativo criado no TikTok for Developers</div>
              </div>
              <div className={classes.formGroup}>
                <label className={classes.label}>App Secret</label>
                <input type="password" value={tiktokAppSecret} onChange={(e) => setTiktokAppSecret(e.target.value)} placeholder="Seu App Secret" className={classes.input} disabled style={{ background: "#f9fafb", cursor: "not-allowed" }} />
                <div className={classes.helpText}>Chave secreta do aplicativo TikTok</div>
              </div>
              <div className={classes.modalActions}>
                <button className={classes.buttonSecondary} onClick={() => setTiktokModalOpen(false)}>Fechar</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Configuração NotificameHub */}
        {notificamehubModalOpen && (
          <div className={classes.modalOverlay} onClick={() => setNotificamehubModalOpen(false)}>
            <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={classes.modalHeader}>
                <h2 className={classes.modalTitle}>🔔 Configurar NotificameHub</h2>
                <button
                  className={classes.closeButton}
                  onClick={() => setNotificamehubModalOpen(false)}
                >
                  <CloseIcon style={{ fontSize: "20px", color: "#6b7280" }} />
                </button>
              </div>

              {notificamehubConfigured && (
                <div className={classes.statusCard}>
                  ✓ NotificameHub configurado e ativo!
                </div>
              )}

              <div className={classes.formGroup}>
                <label className={classes.label}>Token da API</label>
                <input
                  type="password"
                  value={notificamehubToken}
                  onChange={(e) => setNotificamehubToken(e.target.value)}
                  placeholder="Seu token da API NotificameHub"
                  className={classes.input}
                />
                <div className={classes.helpText}>
                  Obtenha o token no painel do NotificameHub: <a href="https://notificamehub.com.br" target="_blank" rel="noopener noreferrer" style={{ color: "#9333ea" }}>notificamehub.com.br</a>
                </div>
              </div>

              <div className={classes.modalActions}>
                <button
                  className={classes.buttonSecondary}
                  onClick={() => {
                    setNotificamehubModalOpen(false);
                    history.push("/notificamehub");
                  }}
                >
                  Ir para NotificameHub
                </button>
                <button
                  className={classes.buttonPrimary}
                  onClick={handleConfigureNotificamehub}
                  disabled={loadingConfig}
                >
                  {loadingConfig ? "Salvando..." : "Salvar Token"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Configuração Cal.com */}
        {configModalOpen && (
          <div className={classes.modalOverlay} onClick={() => setConfigModalOpen(false)}>
            <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={classes.modalHeader}>
                <h2 className={classes.modalTitle}>⚙️ Configurar Cal.com</h2>
                <button
                  className={classes.closeButton}
                  onClick={() => setConfigModalOpen(false)}
                >
                  <CloseIcon style={{ fontSize: "20px", color: "#6b7280" }} />
                </button>
              </div>

              {calcomIntegration?.configured && (
                <div className={classes.statusCard}>
                  ✓ Integração configurada e ativa!
                  {calcomIntegration.baseUrl && (
                    <div style={{ fontSize: "12px", marginTop: "8px", opacity: 0.8 }}>
                      URL: {calcomIntegration.baseUrl}
                    </div>
                  )}
                </div>
              )}

              <div className={classes.formGroup}>
                <label className={classes.label}>API Key do Cal.com</label>
                <input
                  type="password"
                  value={calcomApiKey}
                  onChange={(e) => setCalcomApiKey(e.target.value)}
                  placeholder="cal_live_..."
                  className={classes.input}
                />
                <div className={classes.helpText}>
                  Obtenha em: <a href="https://app.cal.com/settings/developer/api-keys" target="_blank" rel="noopener noreferrer" style={{ color: "#8b5cf6" }}>Settings &gt; Developer &gt; API Keys</a> no Cal.com
                </div>
              </div>

              <div className={classes.formGroup}>
                <label className={classes.label}>Base URL da API</label>
                <input
                  type="text"
                  value={calcomBaseUrl}
                  onChange={(e) => setCalcomBaseUrl(e.target.value)}
                  className={classes.input}
                />
                <div className={classes.helpText}>
                  Padrão: https://api.cal.com/v2
                </div>
              </div>

              <div className={classes.modalActions}>
                <button
                  className={classes.buttonSecondary}
                  onClick={() => setConfigModalOpen(false)}
                >
                  Cancelar
                </button>
                <button
                  className={classes.buttonPrimary}
                  onClick={handleConfigureCalcom}
                  disabled={loadingConfig}
                >
                  {loadingConfig ? "Salvando..." : "Salvar Configuração"}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Modal de Configuração Wavoip */}
        {wavoipModalOpen && (
          <div className={classes.modalOverlay} onClick={() => setWavoipModalOpen(false)}>
            <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={classes.modalHeader}>
                <h2 className={classes.modalTitle}>📞 Configurar Wavoip</h2>
                <button
                  className={classes.closeButton}
                  onClick={() => setWavoipModalOpen(false)}
                >
                  <CloseIcon style={{ fontSize: "20px", color: "#6b7280" }} />
                </button>
              </div>

              {wavoipConfigured && (
                <div className={classes.statusCard}>
                  ✓ Wavoip configurado e ativo!
                </div>
              )}

              <div className={classes.formGroup}>
                <label className={classes.label}>URL Wavoip</label>
                <input
                  type="text"
                  value={wavoipUrl}
                  onChange={(e) => setWavoipUrl(e.target.value)}
                  placeholder="https://api.wavoip.com"
                  className={classes.input}
                  readOnly
                  style={{ background: "#f9fafb", cursor: "not-allowed" }}
                />
                <div className={classes.helpText}>
                  URL padrão da API Wavoip
                </div>
              </div>

              <div className={classes.formGroup}>
                <label className={classes.label}>Email/Usuário</label>
                <input
                  type="email"
                  value={wavoipUsername}
                  onChange={(e) => setWavoipUsername(e.target.value)}
                  placeholder="seu@email.com"
                  className={classes.input}
                />
                <div className={classes.helpText}>
                  Email ou usuário da conta Wavoip: <a href="https://wavoip.com" target="_blank" rel="noopener noreferrer" style={{ color: "#f59e0b" }}>wavoip.com</a>
                </div>
              </div>

              <div className={classes.formGroup}>
                <label className={classes.label}>Senha</label>
                <input
                  type="password"
                  value={wavoipPassword}
                  onChange={(e) => setWavoipPassword(e.target.value)}
                  placeholder="••••••••"
                  className={classes.input}
                />
                <div className={classes.helpText}>
                  Senha da conta Wavoip. Configure os tokens individuais em cada conexão.
                </div>
              </div>

              <div className={classes.modalActions}>
                <button
                  className={classes.buttonSecondary}
                  onClick={() => setWavoipModalOpen(false)}
                >
                  Cancelar
                </button>
                <button
                  className={classes.buttonPrimary}
                  onClick={handleConfigureWavoip}
                  disabled={loadingConfig}
                >
                  {loadingConfig ? "Salvando..." : "Salvar Configuração"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Configuração WhatsApp Oficial */}
        {whatsappOficialModalOpen && (
          <div className={classes.modalOverlay} onClick={() => setWhatsappOficialModalOpen(false)}>
            <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={classes.modalHeader}>
                <h2 className={classes.modalTitle}>💬 Configurar WhatsApp Oficial</h2>
                <button
                  className={classes.closeButton}
                  onClick={() => setWhatsappOficialModalOpen(false)}
                >
                  <CloseIcon style={{ fontSize: "20px", color: "#6b7280" }} />
                </button>
              </div>

              <div style={{
                background: "#fef3c7",
                border: "1px solid #fbbf24",
                borderRadius: "8px",
                padding: "16px",
                color: "#92400e",
                fontSize: "14px",
                marginBottom: "20px",
              }}>
                ⚠️ Esta funcionalidade estará disponível em breve!
              </div>

              <div className={classes.formGroup}>
                <label className={classes.label}>URL da API Oficial</label>
                <input
                  type="text"
                  value={whatsappOficialUrl}
                  onChange={(e) => setWhatsappOficialUrl(e.target.value)}
                  placeholder="https://api.whatsapp.com"
                  className={classes.input}
                  disabled
                  style={{ background: "#f9fafb", cursor: "not-allowed" }}
                />
                <div className={classes.helpText}>
                  URL da API oficial do WhatsApp Business
                </div>
              </div>

              <div className={classes.formGroup}>
                <label className={classes.label}>Token da API</label>
                <input
                  type="password"
                  value={whatsappOficialToken}
                  onChange={(e) => setWhatsappOficialToken(e.target.value)}
                  placeholder="your_token"
                  className={classes.input}
                  disabled
                  style={{ background: "#f9fafb", cursor: "not-allowed" }}
                />
                <div className={classes.helpText}>
                  Token de autenticação da API oficial do WhatsApp
                </div>
              </div>

              <div className={classes.modalActions}>
                <button
                  className={classes.buttonSecondary}
                  onClick={() => setWhatsappOficialModalOpen(false)}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}


      </div>
    </MainContainer>
  );
};

export default Apps;
